import { Container, TextField, Dropdown } from "../../../../shared/ui";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { FC } from "react";
import type { Option } from "../../../../shared/ui";

import { HeroBlock } from "../../../../entities/HeroBlock";
import { Search } from "../../../../shared/assets/svg";
import { vacanciesBanner } from "../../../../shared/assets/img";
import declOfNum from "../../../../shared/lib/declOfNum";
import { useGetDirectionsQuery } from "../../../../entities/Direction";
import { useGetVacancyCityQuery } from "../../../../entities/Vacancy";
import { useForm, Controller } from "react-hook-form";
import { useDebouncedCallback } from "../../../../shared/hooks/useDebouncedCallback";
import type { FormVacancy, SearchVacanciesProps, DropdownValue, TextfieldValue } from "../../types";
import { SearchParamsVacancyEnum } from "../../../../shared/constants/searchParams";

import styles from "./SearchVacancies.module.scss";
import { getRouteDirection, getRouteVacancies } from "../../../../shared/constants/routes";
import { removeNullKeys } from "../../../../shared/lib/helpers";
import { useMediaQuery } from "../../../../shared/hooks/useMediaQuery";
import { useGetJobExperienceQuery } from "../../../../entities/Vacancy/api/vacancyApi";
import { DropdownWithSearch } from "../../../../shared/ui/Dropdown/ui/DropdownWithSearch/DropdownWithSearch";

export const SearchVacancies: FC<SearchVacanciesProps> = ({ countVacancies, onSetParams }) => {
    const isMobile = useMediaQuery(`(max-width: 585px)`);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const searchValue = searchParams.get(SearchParamsVacancyEnum.SEARCH) ?? "";
    const cityValue = searchParams.get(SearchParamsVacancyEnum.CITY) ?? "";
    const directionValue = searchParams.get(SearchParamsVacancyEnum.DIRECTION) ?? "";
    const experienceValue = searchParams.get(SearchParamsVacancyEnum.EXPERIENCE) ?? "";

    const { control, getValues, setValue } = useForm<FormVacancy>({ defaultValues: {} });

    const { directionList, isLoadingDirectionList } = useGetDirectionsQuery(undefined, {
        selectFromResult: ({ data, isLoading }) => ({
            isLoadingDirectionList: isLoading,
            directionList: data?.map((item) => ({ label: item.name!, value: item.id! })) ?? [],
        }),
    });

    const { citiesList, isLoadingCitiesList } = useGetVacancyCityQuery(undefined, {
        selectFromResult: ({ data, isLoading }) => ({
            isLoadingCitiesList: isLoading,
            citiesList:
                data?.data?.reduce<Array<Option>>((prev, item) => {
                    return item ? [...prev, { label: item, value: item }] : prev;
                }, []) ?? [],
        }),
    });

    const { jobExperience, isLoadingJobExperience } = useGetJobExperienceQuery(undefined, {
        selectFromResult: ({ data, isLoading }) => ({
            isLoadingJobExperience: isLoading,
            jobExperience: data?.map((item) => ({ label: item.name!, value: item.id?.toString()! })) ?? [],
        }),
    });

    const handleChangeSearchParams = useDebouncedCallback(() => {
        onSetParams(getValues());
    }, 500);

    const handleChange =
        <T extends DropdownValue | TextfieldValue>(onChange: (value: T) => void) =>
        (value: T) => {
            onChange(value);
            handleChangeSearchParams();
        };

    const handleChangeDirection =
        <T extends DropdownValue | TextfieldValue>(onChange: (value: T) => void) =>
        (value: T) => {
            onChange(value);
            handleChangeSearchParams();
            if (pathname === getRouteVacancies()) {
                navigate({
                    pathname: getRouteDirection(),
                    search: createSearchParams(removeNullKeys(getValues())).toString(),
                });
            }
        };

    const handleRemoveSearch = () => {
        setValue(SearchParamsVacancyEnum.SEARCH, "");
        handleChangeSearchParams();
    };

    const form = (
        <div className={styles.form}>
            <Controller
                defaultValue={searchValue}
                name={SearchParamsVacancyEnum.SEARCH}
                control={control}
                render={({ field: { onChange, ...props } }) => (
                    <TextField
                        className={styles.input}
                        autoComplete="off"
                        placeholder="Название должности"
                        startDecorator={<Search />}
                        onChange={handleChange<TextfieldValue>(onChange)}
                        {...props}
                    />
                )}
            />
            <div className={styles.dropdowns}>
                <Controller
                    defaultValue={experienceValue}
                    name={SearchParamsVacancyEnum.EXPERIENCE}
                    control={control}
                    render={({ field: { onChange, value, ...props } }) => (
                        <Dropdown
                            className={styles.input}
                            selected={value}
                            placeholder="Опыт работы"
                            options={jobExperience}
                            isLoading={isLoadingJobExperience}
                            onChangeOption={handleChange<DropdownValue>(onChange)}
                            {...props}
                        />
                    )}
                />
                <Controller
                    defaultValue={cityValue}
                    name={SearchParamsVacancyEnum.CITY}
                    control={control}
                    render={({ field: { onChange, value, ...props } }) => (
                        <DropdownWithSearch
                            className={styles.input}
                            selected={value}
                            placeholder="Город"
                            isLoading={isLoadingCitiesList}
                            options={citiesList.map((option) =>
                                typeof option === "string" ? { value: option, label: option } : option,
                            )}
                            onChangeOption={handleChange<DropdownValue>(onChange)}
                            {...props}
                        />
                    )}
                />
                <Controller
                    defaultValue={directionValue}
                    name={SearchParamsVacancyEnum.DIRECTION}
                    control={control}
                    render={({ field: { onChange, value, ...props } }) => (
                        <Dropdown
                            className={styles.input}
                            selected={value}
                            isLoading={isLoadingDirectionList}
                            placeholder="Направление"
                            options={directionList}
                            onChangeOption={handleChangeDirection<DropdownValue>(onChange)}
                            {...props}
                        />
                    )}
                />
            </div>
        </div>
    );

    return (
        <Container className={styles.searchVacancies}>
            <HeroBlock imageAttributes={{ src: vacanciesBanner }} title="Вакансии БКС">
                <div className={styles.childrenWrapper}>
                    <div className={styles.header}>
                        <h4 className={styles.countVacancies}>
                            {countVacancies > 0 && (
                                <>
                                    Всего {countVacancies}{" "}
                                    {declOfNum(countVacancies, [" вакансия", " вакансии", " вакансий"])}
                                </>
                            )}
                        </h4>
                        {searchValue && (
                            <button className={styles.button} onClick={handleRemoveSearch}>
                                Очистить фильтр
                                <span className={styles.close} />
                            </button>
                        )}
                    </div>

                    {!isMobile && form}
                </div>
            </HeroBlock>
            {isMobile && form}
        </Container>
    );
};
